import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { motion } from 'framer-motion';
import { FaRocket, FaExternalLinkAlt, FaBrain, FaProjectDiagram, FaUsers, FaMicrophone, FaChartLine, FaShieldAlt } from 'react-icons/fa';
import { SiOpenai } from 'react-icons/si';
import { getProducts } from '../../aws/contentService_lambda';

const ICON_MAP = {
    FaProjectDiagram,
    FaMicrophone,
    FaRocket,
    FaBrain,
    FaUsers,
    FaChartLine,
    FaShieldAlt,
    SiOpenai,
};

const GRADIENT_OPTIONS = [
    'from-violet-600 via-purple-600 to-indigo-700',
    'from-emerald-500 via-teal-600 to-cyan-700',
    'from-blue-600 via-blue-700 to-indigo-800',
    'from-orange-500 via-red-500 to-pink-600',
    'from-rose-500 via-pink-600 to-purple-700',
    'from-cyan-500 via-blue-600 to-violet-700',
];

const GLOW_COLORS = [
    'rgba(139, 92, 246, 0.3)',
    'rgba(20, 184, 166, 0.3)',
    'rgba(59, 130, 246, 0.3)',
    'rgba(249, 115, 22, 0.3)',
    'rgba(244, 63, 94, 0.3)',
    'rgba(6, 182, 212, 0.3)',
];

const staticProducts = [
    {
        id: 'static-1',
        name: 'Stride',
        tagline: 'AI-Native Project Management',
        description: 'A project management platform with AI woven into its core. Stride helps teams plan, track, and deliver projects smarter -- not harder.',
        url: 'https://stride.inferix.in/login',
        icon: 'FaProjectDiagram',
        gradient: 'from-violet-600 via-purple-600 to-indigo-700',
        glowColor: 'rgba(139, 92, 246, 0.3)',
        features: ['AI-Powered Workflows', 'Team Collaboration', 'Smart Analytics', 'Enterprise Ready'],
        techStack: ['Node.js', 'Next.js', 'AI/ML', 'Kubernetes'],
    },
    {
        id: 'static-2',
        name: 'Rehearse',
        tagline: 'AI Mock Interview Coach',
        description: 'Practice interviews with an AI coach that gives real-time feedback. Build confidence and ace your next interview with personalized sessions.',
        url: 'https://reharse.inferix.in/',
        icon: 'FaMicrophone',
        gradient: 'from-emerald-500 via-teal-600 to-cyan-700',
        glowColor: 'rgba(20, 184, 166, 0.3)',
        features: ['AI-Driven Feedback', 'Mock Interviews', 'Performance Tracking', 'Multi-Role Practice'],
        techStack: ['Node.js', 'Next.js', 'AI/ML', 'Kubernetes'],
    },
];

const FEATURE_ICONS = [FaBrain, FaUsers, FaChartLine, FaShieldAlt];

const Products = () => {
    const { theme } = useTheme();
    const [hoveredId, setHoveredId] = useState(null);
    const [products, setProducts] = useState(staticProducts);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProducts = async () => {
            setLoading(true);
            try {
                const apiData = await getProducts();
                const apiProducts = apiData.map((p, i) => ({
                    ...p,
                    gradient: p.gradient || GRADIENT_OPTIONS[i % GRADIENT_OPTIONS.length],
                    glowColor: p.glowColor || GLOW_COLORS[i % GLOW_COLORS.length],
                    features: Array.isArray(p.features) ? p.features : [],
                    techStack: Array.isArray(p.techStack) ? p.techStack : [],
                }));
                const combined = [...staticProducts, ...apiProducts];
                setProducts(combined);
            } catch (error) {
                console.error('Error fetching products:', error);
                setProducts(staticProducts);
            } finally {
                setLoading(false);
            }
        };
        fetchProducts();
    }, []);

    return (
        <section
            className={`section w-full py-16 transition-colors duration-300 ${
                theme === 'dark' ? 'bg-gray-900' : 'bg-gray-100'
            }`}
            id="products"
        >
            <div className="container max-w-7xl mx-auto px-6">
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="text-center mb-14"
                >
                    <h2
                        className={`section__title text-4xl font-bold mb-4 transition-colors duration-300 ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}
                    >
                        My Products
                    </h2>
                    <p
                        className={`text-lg max-w-2xl mx-auto ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}
                    >
                        AI-powered tools I've built to solve real problems
                    </p>
                </motion.div>

                {loading ? (
                    <div className="flex justify-center items-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
                        {products.map((product, index) => {
                            const IconComponent = ICON_MAP[product.icon] || FaRocket;
                            const isHovered = hoveredId === product.id;
                            const gradient = product.gradient || GRADIENT_OPTIONS[index % GRADIENT_OPTIONS.length];
                            const glowColor = product.glowColor || GLOW_COLORS[index % GLOW_COLORS.length];

                            return (
                                <motion.div
                                    key={product.id}
                                    initial={{ opacity: 0, y: 40 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: index * 0.15 }}
                                    onMouseEnter={() => setHoveredId(product.id)}
                                    onMouseLeave={() => setHoveredId(null)}
                                    className="relative group h-full"
                                >
                                    {/* Glow effect */}
                                    <div
                                        className="absolute -inset-1 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl"
                                        style={{ background: glowColor }}
                                    />

                                    <div
                                        className={`relative rounded-2xl overflow-hidden shadow-xl transition-all duration-500 group-hover:shadow-2xl group-hover:-translate-y-2 h-full flex flex-col ${
                                            theme === 'dark'
                                                ? 'bg-gray-800 border border-gray-700'
                                                : 'bg-white border border-gray-200'
                                        }`}
                                    >
                                        {/* Header with gradient */}
                                        <div
                                            className={`relative p-8 bg-gradient-to-br ${gradient} overflow-hidden`}
                                        >
                                            <div className="absolute inset-0 opacity-10">
                                                <div className="absolute top-4 right-4 w-32 h-32 rounded-full border-2 border-white/30 group-hover:scale-150 transition-transform duration-700" />
                                                <div className="absolute bottom-4 left-4 w-20 h-20 rounded-full border-2 border-white/20 group-hover:scale-150 transition-transform duration-700 delay-100" />
                                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 rounded-full border border-white/10 group-hover:scale-125 transition-transform duration-700 delay-200" />
                                            </div>

                                            <div className="relative z-10 flex items-center gap-5">
                                                <motion.div
                                                    className="w-16 h-16 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg"
                                                    animate={isHovered ? { rotate: [0, -10, 10, 0] } : {}}
                                                    transition={{ duration: 0.5 }}
                                                >
                                                    <IconComponent className="text-3xl text-white" />
                                                </motion.div>
                                                <div>
                                                    <h3 className="text-2xl font-bold text-white">
                                                        {product.name}
                                                    </h3>
                                                    <p className="text-white/80 text-sm font-medium">
                                                        {product.tagline}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Body */}
                                        <div className="p-6 flex flex-col flex-grow">
                                            <p
                                                className={`text-sm leading-relaxed mb-6 ${
                                                    theme === 'dark'
                                                        ? 'text-gray-300'
                                                        : 'text-gray-600'
                                                }`}
                                            >
                                                {product.description}
                                            </p>

                                            {/* Features grid */}
                                            {product.features.length > 0 && (
                                                <div className="grid grid-cols-2 gap-3 mb-6">
                                                    {product.features.map((feature, i) => {
                                                        const FeatureIcon = FEATURE_ICONS[i % FEATURE_ICONS.length];
                                                        const label = typeof feature === 'string' ? feature : feature.label;
                                                        return (
                                                            <motion.div
                                                                key={i}
                                                                initial={{ opacity: 0, scale: 0.9 }}
                                                                whileInView={{ opacity: 1, scale: 1 }}
                                                                viewport={{ once: true }}
                                                                transition={{ delay: 0.3 + i * 0.1 }}
                                                                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors duration-300 ${
                                                                    theme === 'dark'
                                                                        ? 'bg-gray-700/50 text-gray-300'
                                                                        : 'bg-gray-50 text-gray-700'
                                                                }`}
                                                            >
                                                                <FeatureIcon className="text-sm flex-shrink-0 opacity-70" />
                                                                <span>{label}</span>
                                                            </motion.div>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            {/* Tech stack */}
                                            {product.techStack.length > 0 && (
                                                <div className="flex flex-wrap gap-2 mb-6">
                                                    {product.techStack.map((tech, i) => (
                                                        <span
                                                            key={i}
                                                            className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                                                                theme === 'dark'
                                                                    ? 'bg-gray-700 text-gray-400 border border-gray-600'
                                                                    : 'bg-gray-100 text-gray-500 border border-gray-200'
                                                            }`}
                                                        >
                                                            {tech}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}

                                            {/* CTA Button */}
                                            <a
                                                href={product.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={`mt-auto w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-300 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] bg-gradient-to-r ${gradient} text-white`}
                                            >
                                                <FaRocket className="text-sm" />
                                                Visit {product.name}
                                                <FaExternalLinkAlt className="text-xs ml-1" />
                                            </a>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                )}
            </div>
        </section>
    );
};

export default Products;
